import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import { storageService } from '../../lib/storageService'
import * as FiIcons from 'react-icons/fi'

const { FiZap, FiUpload, FiMoreVertical, FiEye, FiTrash2, FiEdit3, FiCheck } = FiIcons

const ImageCard = ({ 
  image, 
  onSelect, 
  onDelete, 
  onUpdate,
  isSelected = false,
  showActions = true 
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [imageError, setImageError] = useState(false)

  const handleSelect = () => {
    if (onSelect) {
      onSelect(image)
    }
  }

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      setIsLoading(true)
      try {
        await onDelete(image.id)
      } catch (error) {
        console.error('Error deleting image:', error)
        alert('Failed to delete image')
      } finally {
        setIsLoading(false)
        setShowMenu(false)
      }
    }
  }

  const getSourceIcon = () => {
    switch (image.source_type || 'upload') {
      case 'ai_generated':
        return FiZap
      case 'upload':
        return FiUpload
      default:
        return FiUpload
    }
  }

  const getSourceColor = () => {
    switch (image.source_type || 'upload') {
      case 'ai_generated':
        return 'text-purple-600 bg-purple-100'
      case 'upload':
        return 'text-blue-600 bg-blue-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return 'Today'
    } else if (diffInHours < 48) {
      return 'Yesterday'
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  // Get thumbnail URL with fallback to original
  const getThumbnailUrl = () => {
    // For AI generated images or images without file_path, use the direct URL
    if (image.source_type === 'ai_generated' || !image.file_path) {
      return image.file_url
    }
    
    // For uploaded images, try to get thumbnail if available
    if (image.file_path && storageService.getThumbnailUrl) {
      return storageService.getThumbnailUrl(image.file_path, 300, 300)
    }
    
    // Fallback to original URL
    return image.file_url
  }

  return (
    <div className={`relative group bg-white rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
      isSelected 
        ? 'border-blue-500 ring-2 ring-blue-200' 
        : 'border-gray-200 hover:border-gray-300'
    }`}>
      {/* Selection Overlay */}
      {isSelected && (
        <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-lg z-10 pointer-events-none">
          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
            <SafeIcon icon={FiCheck} className="w-3 h-3" />
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg z-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Image */}
      <div 
        className="aspect-square bg-gray-100 rounded-t-lg overflow-hidden cursor-pointer"
        onClick={handleSelect}
      >
        {!imageError ? (
          <img
            src={getThumbnailUrl()}
            alt={image.alt_text || image.file_name}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <SafeIcon icon={FiEye} className="w-8 h-8" />
          </div>
        )}
      </div>

      {/* Image Info */}
      <div className="p-3">
        {/* Top Row: Source + Actions */}
        <div className="flex items-center justify-between mb-2">
          <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getSourceColor()}`}>
            <SafeIcon icon={getSourceIcon()} className="w-3 h-3" />
            <span className="capitalize">{(image.source_type || 'upload').replace('_', ' ')}</span>
          </div>

          {showActions && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMenu(!showMenu)
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
              </button>

              {/* Action Menu */}
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-30 min-w-[120px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSelect()
                      setShowMenu(false)
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="w-3 h-3" />
                    <span>Select</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      // TODO: Implement rename functionality
                      setShowMenu(false)
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiEdit3} className="w-3 h-3" />
                    <span>Rename</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete()
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* File Name */}
        <div className="mb-2">
          <p className="text-sm font-medium text-gray-900 truncate" title={image.file_name}>
            {image.file_name}
          </p>
        </div>

        {/* File Details */}
        <div className="space-y-1 text-xs text-gray-500">
          {image.width && image.height && (
            <p>{image.width} × {image.height}px</p>
          )}
          {image.file_size && (
            <p>{storageService.formatBytes(image.file_size)}</p>
          )}
          <p>{formatDate(image.created_at)}</p>
        </div>

        {/* Usage Count */}
        {image.usage_count > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            Used {image.usage_count} time{image.usage_count !== 1 ? 's' : ''}
          </div>
        )}

        {/* Description Preview */}
        {image.description && (
          <div className="mt-2 text-xs text-gray-600 truncate" title={image.description}>
            {image.description}
          </div>
        )}

        {/* Tags */}
        {image.tags && image.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {image.tags.slice(0, 2).map((tag, index) => (
              <span 
                key={index}
                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {tag}
              </span>
            ))}
            {image.tags.length > 2 && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                +{image.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Click Outside Handler */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-20" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  )
}

export default ImageCard
